// MIT License - Copyright (c) 2026 Doug McNall
import { useAccessControl } from '../hooks/useAccessControl';
import { AlertTriangleIcon, ShieldIcon } from './Icons';
import LoadingSpinner from './LoadingSpinner';
import './TabAccessGuard.css';
import './Icons.css';

/**
 * Component to guard individual admin tabs based on user permissions
 * Wraps tab content and shows access denied if user lacks permissions
 */
const TabAccessGuard = ({ 
  children, 
  tabId, 
  requiredAction = 'read',
  fallbackMessage = null 
}) => {
  const { 
    loading, 
    hasTabAccess, 
    canPerformAction, 
    userGroupAssignments,
    isSuperUser
  } = useAccessControl();

  // Get user role description for display
  const getUserRoleDescription = () => {
    if (isSuperUser()) {
      return 'Super Administrator';
    }
    
    if (!Array.isArray(userGroupAssignments) || userGroupAssignments.length === 0) {
      return 'No tab assignments';
    }
    
    const adminTabs = userGroupAssignments.filter(a => a.role === 'group_admin').length;
    const viewerTabs = userGroupAssignments.filter(a => a.role === 'viewer').length;
    
    const parts = [];
    if (adminTabs > 0) parts.push(`Admin of ${adminTabs} tab(s)`);
    if (viewerTabs > 0) parts.push(`Viewer of ${viewerTabs} tab(s)`);
    
    return parts.join(', ') || 'Unknown role';
  };

  // Restrict API test tab to super users only
  if (tabId === 'api-test') {
    if (loading) {
      return <LoadingSpinner message="Checking permissions..." />;
    }
    
    if (!isSuperUser()) {
      return (
        <div className="tab-access-denied">
          <div className="access-denied-content">
            <div className="access-denied-icon">
              <ShieldIcon size={48} className="icon-warning" />
            </div>
            <h2>Super User Access Required</h2>
            <p>
              The <strong>API Test</strong> section is restricted to Super Users only for security purposes.
            </p>
            <p className="user-role">
              Your role: <span className="role-badge">{getUserRoleDescription()}</span>
            </p>
            <div className="permission-hint">
              <p>This tool provides direct access to all API endpoints and is reserved for system administrators.</p>
            </div>
            <div className="access-denied-actions">
              <button 
                onClick={() => window.history.back()}
                className="btn-secondary"
              >
                Go Back
              </button>
              <button 
                onClick={() => window.location.href = '/admin-tool'}
                className="btn-primary"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Super user has access to API test page
    return children;
  }

  // Show loading while checking permissions
  if (loading) {
    return <LoadingSpinner message="Checking permissions..." />;
  }

  // Check if user has basic access to this tab
  if (!hasTabAccess(tabId)) {
    return (
      <div className="tab-access-denied">
        <div className="access-denied-content">
          <div className="access-denied-icon">
            <ShieldIcon size={48} className="icon-warning" />
          </div>
          <h2>Access Restricted</h2>
          <p>
            You don't have permission to access the <strong>{getTabDisplayName(tabId)}</strong> section.
          </p>
          <p className="user-role">
            Your role: <span className="role-badge">{getUserRoleDescription()}</span>
          </p>
          {fallbackMessage && (
            <div className="fallback-message">
              {fallbackMessage}
            </div>
          )}
          <div className="access-denied-actions">
            <button 
              onClick={() => window.history.back()}
              className="btn-secondary"
            >
              Go Back
            </button>
            <button 
              onClick={() => window.location.href = '/admin-tool'}
              className="btn-primary"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user can perform the required action
  if (!canPerformAction(tabId, requiredAction)) {
    return (
      <div className="tab-access-denied">
        <div className="access-denied-content">
          <div className="access-denied-icon">
            <AlertTriangleIcon size={48} className="icon-warning" />
          </div>
          <h2>Insufficient Permissions</h2>
          <p>
            You can view the <strong>{getTabDisplayName(tabId)}</strong> section, 
            but you don't have permission to <strong>{requiredAction}</strong> data.
          </p>
          <p className="user-role">
            Your role: <span className="role-badge">{getUserRoleDescription()}</span>
          </p>
          <div className="permission-hint">
            <p>Contact your administrator to request additional permissions.</p>
          </div>
          <div className="access-denied-actions">
            <button 
              onClick={() => window.history.back()}
              className="btn-secondary"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User has required permissions - render the protected content
  return children;
};

// Helper function to get user-friendly tab names
const getTabDisplayName = (tabId) => {
  const tabNames = {
    'dashboard': 'Dashboard',
    'products': 'Products',
    'categories': 'Categories', 
    'orders': 'Orders',
    'customers': 'Customers',
    'coupons': 'Coupons',
    'users': 'Users',
    'recipes': 'Recipes',
    'reviews': 'Reviews',
    'manufacturing': 'Manufacturing',
    'shipping': 'Shipping',
    'payment-gateways': 'Payment Gateways',
    'revenue': 'Revenue',
    'api-test': 'API Test'
  };
  
  return tabNames[tabId] || tabId;
};

export default TabAccessGuard;